import {
  Directive,
  inject,
  Injectable,
  input,
  OnDestroy,
  Signal,
  signal,
  TemplateRef,
} from '@angular/core';

/**
 * Hands bar zones from the routed page to the shell: the bar sits above the outlet, so no projection.
 * A declared end replaces the signed-in user's identity rather than sharing its place.
 */
@Injectable({ providedIn: 'root' })
export class HeaderSlot {
  private readonly currentMiddle = signal<HeaderMiddleZone | null>(null);
  private readonly currentEnd = signal<HeaderZone | null>(null);

  readonly middle = this.currentMiddle.asReadonly();
  readonly end = this.currentEnd.asReadonly();

  attachMiddle(zone: HeaderMiddleZone): void {
    this.currentMiddle.set(zone);
  }

  attachEnd(zone: HeaderZone): void {
    this.currentEnd.set(zone);
  }

  // On navigation the new page can attach before the old one is destroyed: only clear our own.
  detachMiddle(zone: HeaderMiddleZone): void {
    if (this.currentMiddle() === zone) {
      this.currentMiddle.set(null);
    }
  }

  detachEnd(zone: HeaderZone): void {
    if (this.currentEnd() === zone) {
      this.currentEnd.set(null);
    }
  }
}

/** What a zone directive hands over: the template the bar instantiates. */
interface HeaderZone {
  readonly template: TemplateRef<unknown>;
}

interface HeaderMiddleZone extends HeaderZone {
  readonly fillsBar: Signal<boolean>;
}

/** Declares the content of the bar's middle zone, from inside a routed page. */
@Directive({ selector: 'ng-template[maiHeaderMiddle]' })
export class HeaderMiddleDirective implements OnDestroy {
  private readonly slot = inject(HeaderSlot);

  readonly template = inject<TemplateRef<unknown>>(TemplateRef);
  /** Takes the whole bar: the brand, the tabs and the end step aside. */
  readonly fillsBar = input(false);

  constructor() {
    this.slot.attachMiddle(this);
  }

  ngOnDestroy(): void {
    this.slot.detachMiddle(this);
  }
}

/** Declares the content of the bar's end zone, in place of the signed-in user's identity. */
@Directive({ selector: 'ng-template[maiHeaderEnd]' })
export class HeaderEndDirective implements OnDestroy {
  private readonly slot = inject(HeaderSlot);

  readonly template = inject<TemplateRef<unknown>>(TemplateRef);

  constructor() {
    this.slot.attachEnd(this);
  }

  ngOnDestroy(): void {
    this.slot.detachEnd(this);
  }
}
